import { ReactElement, useState, useEffect } from 'react';
import { FormikContextType } from 'formik';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import { Button } from '@upmc/ims.component-library';
import Icon from '../../../../components/UI/MDC/Icon/Icon';
import ModelAfter from '../../../../components/UI/Input/InputElements/ModelAfter/ModelAfter';
import withComponentLoader from '../../../../hoc/withComponentLoader';
import AppWrappedInput from './AppWrappedInput';
import { EPIC_APP_ID, EPIC_FIELD_KEYS } from './79/_const';
import PersonMetadata from './PersonMetadata';
import AppWrappedTextbox from './AppWrappedTextbox';
import InvisibilityCloak from './InvisibilityCloak';
import FunctionalRoleHelperLinks from './79/components/FunctionalRoleHelperLinks';
import PositionHelperLinks from './79/components/PositionHelperLinks';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';

/**
 * Model-After Person-Search input base component stylized to work with Custom App Request Form
 * @param {Object} props component properties
 * @param {Function} props.setLoading setter function coming from `withComponentLoader` HOC
 * @param {string} props.label display text
 * @param {FormikContextType} props.formik full Formik Context
 * @param {string|ReactElement} props.helperText helper text
 * @param {object} props.affiliateDepartmentList List of affiliate departments
 * @param {boolean} props.isRequired flag to determine whether to show required indicator
 * @param {boolean} props.isLoading flag to indicate the loading state (comes from parent)
 * @param {object} props.props the rest of the props
 * @returns Model-After Person-Search input stylized for custom
 */
const AppWrappedModelAfter = ({
	setLoading,
	label,
	formik,
	helperText,
	affiliateDepartmentList,
	isRequired = false,
	isLoading = false,
	...props
}) => {
	const [modelAfterMetadata, setModelAfterMetadata] = useState(null);
	const [modelAfterUserRejected, setModelAfterUserRejected] = useState(null);
	const [modelAfterUserSelected, setModelAfterUserSelected] = useState('');

	useEffect(() => {
		let rejected = isModelAfterUserRejected();

		if (!rejected) {
			formik.setFieldValue(props.name, modelAfterUserSelected);
		}
		setModelAfterUserRejected(rejected);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modelAfterMetadata]);

	const shouldShowDeaField = () => {
		const positionKVP = modelAfterMetadata?.metadata?.find(
			x => x.Key === 'Current EpicCare Position',
		);
		if (positionKVP) {
			const position = positionKVP?.Value?.toLowerCase();
			return (
				position.includes('w/ dea') ||
				position.includes('w/dea') ||
				props.shouldShowDeaField(
					position?.split('[')[0].trim().toLowerCase(),
				)
			);
		} else {
			return false;
		}
	};

	useEffect(() => {
		setLoading(isLoading);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoading]);

	const handleChange = e => {
		setModelAfterUserSelected(e);
	};

	const isModelAfterUserRejected = () => {
		if (modelAfterMetadata) {
			let currentSchedulingRole = modelAfterMetadata.metadata
				?.find(item => item.Key === 'Current Epic Scheduling Role')
				?.Value.toLowerCase();
			let currentEpicImagingSchedulingRole =
				modelAfterMetadata.metadata
					?.find(
						item =>
							item.Key === 'Current Epic Imaging Scheduling Role',
					)
					?.Value?.toLowerCase() ?? null;

			let currentLoginDepartment = modelAfterMetadata.metadata?.find(
				item => item.Key === 'Current Default Login Department',
			).Value;

			let isCurrentDeptAffiliate = affiliateDepartmentList.some(
				department =>
					department.departmentId.trim() ===
					currentLoginDepartment.trim(),
			);

			// Reject the user if the Current Epic Imaging Scheduling Role
			// does not exist on the object returned from API
			if (currentEpicImagingSchedulingRole === null) {
				formik.setFieldValue(props.name, '');
				return true;
			}

			// Reject if imaging role is Imaging Schedule w/Overbook,Override and Edit Slot
			// If their role is one of these 3 and also if the default login department is not an affiliate, reject
			const schedulingRolesToReject = [
				'confidential enhanced scheduling w/edit slots',
				'confidential enhanced scheduling w/ edit slots',
				'enhanced scheduling w/edit slots',
				'scheduling template builder',
				'confidential scheduling template builder',
			];

			const imagingRolesToReject = ['imaging schedule w/edit slot'];

			if (
				(schedulingRolesToReject.includes(currentSchedulingRole) ||
					imagingRolesToReject.includes(
						currentEpicImagingSchedulingRole,
					)) &&
				!isCurrentDeptAffiliate
			) {
				formik.setFieldValue(props.name, '');
				return true;
			}
		}
		return false;
	};

	return (
		<AppWrappedInput
			label={label}
			error={
				formik.touched[props.name] ? formik.errors[props.name] : null
			}
			isRequired={isRequired}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'flex-start',
					alignItems: 'stretch',
				}}
			>
				{modelAfterUserRejected && (
					<>
						<Alert severity='error'>
							<AlertTitle>User Not Selected</AlertTitle>
							This person has advanced scheduling access that
							requires additional training. Please select a
							different user, or manually select lower security.
							You may sign your staff up for training to get the
							advanced scheduling access once training is
							complete.{' '}
						</Alert>
						<Button
							endIcon={<Icon icon='delete' />}
							onClick={() => {
								setModelAfterUserSelected('');
								setModelAfterMetadata(null);
							}}
							variant='outlined'
						>
							Select a different user
						</Button>
					</>
				)}

				{formik.values[props.name] && (
					<Alert
						severity='warning'
						sx={{
							borderStyle: 'solid',
							borderColor: 'black',
							borderWidth: 'thin',
							borderRadius: '4px',
						}}
					>
						<AlertTitle>Attention!</AlertTitle>
						You have chosen to model your user after the user listed
						below.{' '}
						<strong>
							This should not be used for practice operations
							staff.
						</strong>{' '}
						<br />
						This will mirror all user access. Please review their
						listed Registration/Billing/Scheduling and/or EpicCare
						Position and verify it is appropriate before proceeding.
					</Alert>
				)}

				{!modelAfterUserRejected && modelAfterUserSelected && (
					<PersonMetadata
						target={modelAfterUserSelected}
						reportMetadata={setModelAfterMetadata}
					/>
				)}

				{formik.values[props.name] && (
					<>
						<Button
							endIcon={<Icon icon='delete' />}
							onClick={() => {
								formik.setFieldValue(props.name, '');
								setModelAfterMetadata(null);
								setModelAfterUserSelected('');
								setModelAfterUserRejected(false);
							}}
							variant='outlined'
						>
							Select a different user
						</Button>

						<FormHelperText>
							<>
								<FunctionalRoleHelperLinks />
								<br />
								<PositionHelperLinks />
							</>
						</FormHelperText>
					</>
				)}
				{!modelAfterUserRejected && modelAfterUserSelected === '' && (
					<FormControl variant='filled' fullWidth>
						<ModelAfter
							appId={EPIC_APP_ID}
							name={props.name}
							id={props.name}
							key={props.name}
							changeHandler={e => {
								handleChange(e.target.value);
							}}
							withoutFormik={true}
							onBlur={() =>
								formik.setFieldTouched(props.name, true)
							}
						/>
						<FormHelperText>{helperText}</FormHelperText>
					</FormControl>
				)}
				<InvisibilityCloak
					name={EPIC_FIELD_KEYS.SHOW_DEA_NUMBER_MIRROR}
					dependentValues={[modelAfterMetadata]}
					handleCloak={setShow => {
						let showTest = shouldShowDeaField();
						setShow(showTest);
						formik.setFieldValue(
							EPIC_FIELD_KEYS.SHOW_DEA_NUMBER_MIRROR,
							showTest,
						);
					}}
				>
					<AppWrappedTextbox
						name={EPIC_FIELD_KEYS.DEA_NUMBER_MIRROR}
						isRequired
						label='DEA Number'
						helperText={
							'The user you have selected to model your roles after has a position that requires you to input your DEA number.'
						}
						formik={formik}
					/>
				</InvisibilityCloak>
			</Box>
		</AppWrappedInput>
	);
};
export default withComponentLoader(AppWrappedModelAfter);
